import { useState, useEffect } from "react";
import { Modal } from "react-bootstrap";
import { onAuthStateChanged } from "firebase/auth";
import { auth } from "../../../lib/config/firebaseInit";
import { OrderItem } from "../../../components/OrderItem";
import { fetchDeliveredOrders } from "../../../lib/webService/webService";
import { useNavigate, useLocation } from "react-router-dom";
import { toast } from "../../../utilities/toast";
import { goToLogin } from "../../../utilities/authRedirect";
import {
  Truck,
  CheckCircleFill,
  ExclamationTriangle,
  ArrowClockwise,
  XLg,
} from "react-bootstrap-icons";
import { PullToRefreshWrapper } from "../../../components/pullRefresh";
import { Seo } from "../../../components/Seo";
import { generateNoIndexSEO } from "../../../lib/seo/metaGenerators";

if (typeof document !== "undefined" && !document.getElementById("od-styles")) {
  const s = document.createElement("style");
  s.id = "od-styles";
  s.textContent = `
    .od-wrap{font-family:-apple-system,BlinkMacSystemFont,"SF Pro Display","Helvetica Neue",Arial,sans-serif;padding:0 4px}
    .od-card{background:rgba(255,255,255,.88);border:1px solid rgba(15,23,42,.06);border-radius:16px;padding:16px;cursor:pointer;transition:transform .22s cubic-bezier(.34,1.24,.64,1),box-shadow .22s;margin-bottom:12px}
    .od-card:hover{transform:translateY(-2px);box-shadow:0 8px 24px rgba(0,0,0,.08)}
    .od-card:active{transform:scale(.985)}
    .od-pill{display:inline-flex;align-items:center;gap:4px;padding:3px 10px;border-radius:20px;font-size:.75rem;font-weight:600}
    .od-modal .modal-content{border-radius:20px!important;border:none!important;overflow:hidden}
    .od-info-grid{display:grid;grid-template-columns:1fr 1fr;gap:16px;background:rgba(118,118,128,.07);border-radius:12px;padding:16px;margin-bottom:20px}
    .od-info-lbl{font-size:.75rem;color:rgba(60,60,67,.5);font-weight:500;margin-bottom:3px}
    .od-info-val{font-size:.9rem;color:#1c1c1e;font-weight:500}
    @keyframes _od_sk{0%,100%{opacity:1}50%{opacity:.4}}
    ._od_sk{animation:_od_sk 1.8s ease-in-out infinite;background:rgba(60,60,67,.08)}
  `;
  document.head.appendChild(s);
}

const FONT = `-apple-system, BlinkMacSystemFont, "SF Pro Display", "Helvetica Neue", Arial, sans-serif`;

const STATUS_CONFIG = {
  delivered:  { color: '#34C759', label: 'Delivered' },
  completed:  { color: '#34C759', label: 'Completed' },
  processing: { color: '#FF9500', label: 'Processing' },
};

const StatusPill = ({ status }) => {
  const cfg = STATUS_CONFIG[status?.toLowerCase()] ?? { color: '#8E8E93', label: status };
  return (
    <span className="od-pill" style={{ backgroundColor: `${cfg.color}1A`, color: cfg.color }}>
      {cfg.label}
    </span>
  );
};

const SkeletonCard = () => (
  <div className="od-card" style={{ cursor: "default" }}>
    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10 }}>
      <div className="_od_sk" style={{ width: 80, height: 12, borderRadius: 4 }} />
      <div className="_od_sk" style={{ width: 72, height: 22, borderRadius: 20 }} />
    </div>
    <div className="_od_sk" style={{ width: "55%", height: 18, borderRadius: 5, marginBottom: 8 }} />
    <div className="_od_sk" style={{ width: "38%", height: 13, borderRadius: 4, marginBottom: 6 }} />
    <div className="_od_sk" style={{ width: "30%", height: 11, borderRadius: 4 }} />
  </div>
);

const EmptyState = () => (
  <div style={{ textAlign: "center", padding: "48px 16px", background: "rgba(255,255,255,.88)", border: "1px solid rgba(15,23,42,.06)", borderRadius: 16 }}>
    <div style={{ width: 56, height: 56, borderRadius: "50%", background: "rgba(142,142,147,.10)", display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 16px" }}>
      <Truck size={26} color="#8E8E93" />
    </div>
    <div style={{ fontSize: "1.0625rem", fontWeight: 600, color: "#1c1c1e", marginBottom: 6 }}>No Deliveries Yet</div>
    <div style={{ fontSize: "0.875rem", color: "rgba(60,60,67,0.55)", lineHeight: 1.5 }}>
      Completed deliveries will appear here.
    </div>
  </div>
);

const ErrorState = ({ error, onRetry }) => (
  <div style={{ textAlign: "center", padding: "48px 16px", background: "rgba(255,255,255,.88)", border: "1px solid rgba(15,23,42,.06)", borderRadius: 16 }}>
    <div style={{ width: 56, height: 56, borderRadius: "50%", background: "rgba(255,59,48,.10)", display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 16px" }}>
      <ExclamationTriangle size={26} color="#FF3B30" />
    </div>
    <div style={{ fontSize: "1.0625rem", fontWeight: 600, color: "#1c1c1e", marginBottom: 6 }}>Something went wrong</div>
    <div style={{ fontSize: "0.875rem", color: "rgba(60,60,67,0.55)", marginBottom: 20, lineHeight: 1.5 }}>
      {error || "We couldn't load your deliveries."}
    </div>
    <button
      onClick={onRetry}
      style={{ display: "inline-flex", alignItems: "center", gap: 6, padding: "10px 22px", background: "#007AFF", color: "#fff", border: "none", borderRadius: 12, fontSize: ".9rem", fontWeight: 600, cursor: "pointer", fontFamily: FONT }}
    >
      <ArrowClockwise size={16} /> Try Again
    </button>
  </div>
);

const Deliveries = () => {
  const [deliveries, setDeliveries] = useState([]);
  const [deliveriesLoading, setDeliveriesLoading] = useState(true);
  const [error, setError] = useState(null);
  const [retryCount, setRetryCount] = useState(0);
  const navigate = useNavigate();
  const location = useLocation();

  const fetchDeliveries = async (uid, token) => {
    try {
      setDeliveriesLoading(true);
      setError(null);
      const data = await fetchDeliveredOrders(uid, token);
      setDeliveries(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error("Error fetching deliveries:", error);
      setError("Failed to load deliveries. Please try again.");
      toast.error("Failed to load deliveries. Please try again.");
      setDeliveries([]);
    } finally {
      setDeliveriesLoading(false);
    }
  };

  useEffect(() => {
    async function checkAuthAndFetch() {
      onAuthStateChanged(auth, async (user) => {
        if (user) {
          try {
            const idTokenResult = await user.getIdTokenResult();
            if (idTokenResult.claims.operations) {
              await fetchDeliveries(user.uid, idTokenResult.token);
            } else {
              toast.error("Access denied. Operations role required.");
              goToLogin(navigate, location);
            }
          } catch (error) {
            console.error("Error checking user claims:", error);
            setError("Authentication error. Please try again.");
          }
        } else {
          toast.info("Session expired. Please login to continue.");
          goToLogin(navigate, location);
        }
      });
    }
    checkAuthAndFetch();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [navigate, retryCount]);

  return (
    <PullToRefreshWrapper onRefresh={async () => fetchDeliveries(auth.currentUser?.uid, await auth.currentUser?.getIdToken())}>
    <Seo {...generateNoIndexSEO("Deliveries")} />
    <div className="od-wrap" style={{ fontFamily: FONT }}>
      <div style={{ marginBottom: 24 }}>
        <div style={{ fontSize: "1.375rem", fontWeight: 700, color: "#1c1c1e", letterSpacing: "-0.01em" }}>
          Delivery History
        </div>
        <div style={{ fontSize: "0.875rem", color: "rgba(60,60,67,0.55)", marginTop: 4 }}>
          View your completed deliveries and their details
        </div>
      </div>

      {deliveriesLoading ? (
        <>{[1, 2, 3].map((i) => <SkeletonCard key={i} />)}</>
      ) : error ? (
        <ErrorState error={error} onRetry={() => setRetryCount((p) => p + 1)} />
      ) : !Array.isArray(deliveries) || deliveries.length === 0 ? (
        <EmptyState />
      ) : (
        deliveries.map((item) => <DeliveryItem key={item.id} {...item} />)
      )}
    </div>
    </PullToRefreshWrapper>
  );
};

function DeliveryItem({ id, clientName, phone, status, cart, updatedAt }) {
  const [showModal, setShowModal] = useState(false);

  return (
    <>
      <div className="od-card" onClick={() => setShowModal(true)}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10 }}>
          <span style={{ fontSize: "0.78rem", color: "rgba(60,60,67,0.45)", fontWeight: 500 }}>#{id}</span>
          <StatusPill status={status} />
        </div>

        <div style={{ fontSize: "1.0625rem", fontWeight: 600, color: "#1c1c1e", marginBottom: 4 }}>
          {clientName}
        </div>
        <div style={{ fontSize: "0.875rem", color: "rgba(60,60,67,0.55)", marginBottom: 6 }}>
          {phone}
        </div>
        <div style={{ fontSize: "0.78rem", color: "rgba(60,60,67,0.38)" }}>
          {updatedAt}
        </div>
      </div>

      <OrderDetailsModal
        show={showModal}
        onHide={() => setShowModal(false)}
        cartItems={cart}
        orderInfo={{ id, clientName, phone, status, updatedAt }}
      />
    </>
  );
}

function OrderDetailsModal({ show, onHide, cartItems, orderInfo }) {
  return (
    <Modal show={show} onHide={onHide} centered dialogClassName="od-modal">
      <Modal.Body style={{ padding: "28px 24px", fontFamily: FONT }}>

        {/* Header */}
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 20 }}>
          <div>
            <div style={{ fontSize: "1.125rem", fontWeight: 700, color: "#1c1c1e" }}>Order Details</div>
            <StatusPill status={orderInfo.status} />
          </div>
          <button
            onClick={onHide}
            style={{ width: 30, height: 30, borderRadius: "50%", background: "rgba(118,118,128,.12)", border: "none", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", color: "rgba(60,60,67,0.55)", flexShrink: 0 }}
          >
            <XLg size={13} />
          </button>
        </div>

        {/* Customer info */}
        <div style={{ fontSize: "0.72rem", fontWeight: 600, color: "rgba(60,60,67,0.55)", textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: 10 }}>
          Customer Information
        </div>
        <div className="od-info-grid">
          <div>
            <div className="od-info-lbl">Name</div>
            <div className="od-info-val">{orderInfo.clientName}</div>
          </div>
          <div>
            <div className="od-info-lbl">Phone</div>
            <div className="od-info-val">{orderInfo.phone}</div>
          </div>
          <div>
            <div className="od-info-lbl">Order ID</div>
            <div className="od-info-val">#{orderInfo.id}</div>
          </div>
          <div>
            <div className="od-info-lbl">Completed On</div>
            <div className="od-info-val">{orderInfo.updatedAt}</div>
          </div>
        </div>

        {/* Order items */}
        <div style={{ fontSize: "0.72rem", fontWeight: 600, color: "rgba(60,60,67,0.55)", textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: 12 }}>
          Order Items
        </div>
        <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
          {cartItems.map((item) => (
            <OrderItem key={item.id} {...item} />
          ))}
        </div>

        {/* Close */}
        <button
          onClick={onHide}
          style={{ width: "100%", marginTop: 24, padding: "12px", background: "rgba(118,118,128,.1)", border: "none", borderRadius: 12, fontSize: ".9375rem", fontWeight: 600, color: "#1c1c1e", cursor: "pointer", fontFamily: FONT }}
        >
          Close
        </button>

      </Modal.Body>
    </Modal>
  );
}

export default Deliveries;
